import { useGeographicConfirmation } from './useGeographicConfirmation';
import { useEffect, useRef, useState } from 'react';
import { consultantReports, type ConsultantReportContent, type ConsultantReportInputs, type ConsultantReportService,
    type ReportDraft, type ReportEmailSummary, type ReportOrganization, type ReportTargets } from '../common/consultantReports';

export const emptyReportInputs: ConsultantReportInputs = { actor: '', country: '', assets: '', context: '' };
const emptyTargets: ReportTargets = { users: [], requests: [], chats: [] };

export function useConsultantReports(clientOrganizationId = '', initialDraftId = '') {
    const [organizations, setOrganizations] = useState<ReportOrganization[]>([]);
    const [services, setServices] = useState<ConsultantReportService[]>([]);
    const [drafts, setDrafts] = useState<ReportDraft[]>([]);
    const [draft, setDraft] = useState<ReportDraft | null>(null);
    const [organizationId, setOrganizationId] = useState(clientOrganizationId);
    const [serviceId, setServiceId] = useState('');
    const [inputs, setInputs] = useState<ConsultantReportInputs>(emptyReportInputs);
    const [report, setReport] = useState<ConsultantReportContent | null>(null);
    const [targets, setTargets] = useState<ReportTargets>(emptyTargets);
    const [emails, setEmails] = useState<ReportEmailSummary | null>(null);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const geography = useGeographicConfirmation('consultant', inputs, draft?.id || serviceId, draft?.geographicReview);
    const generation = useRef<{ draftId: string; operationId: string } | null>(null);
    const dirty = Boolean(report && JSON.stringify(report) !== JSON.stringify(draft?.report));
    const inputsChanged = Boolean(draft && JSON.stringify(inputs) !== JSON.stringify(draft.inputs));

    const loadList = async () => {
        if (!initialDraftId)
            setDrafts((await consultantReports<{ drafts: ReportDraft[] }>('list')).drafts);
    };
    useEffect(() => {
        let active = true;
        void Promise.all([consultantReports<{ organizations: ReportOrganization[]; services: ConsultantReportService[] }>('configuration'),
            initialDraftId ? consultantReports<{ draft: ReportDraft; emails: ReportEmailSummary }>('load', { draftId: initialDraftId })
                : consultantReports<{ drafts: ReportDraft[] }>('list')]).then(([configuration, result]) => {
            if (active) {
                setOrganizations(configuration.organizations);
                setServices(configuration.services);
                if ('draft' in result)
                    accept(result.draft, result.emails);
                else
                    setDrafts(result.drafts);
            }
        }).catch(failure => active && setError(failure.message)).finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [initialDraftId]);

    useEffect(() => {
        let active = true;
        setTargets(emptyTargets);
        if (organizationId)
            void consultantReports<ReportTargets>('targets', { organizationId }).then(result => active && setTargets(result))
                .catch(failure => active && setError(failure.message));
        return () => { active = false; };
    }, [organizationId]);

    const accept = (next: ReportDraft, summary?: ReportEmailSummary) => {
        setDraft(next);
        setReport(next.report || null);
        setOrganizationId(next.organizationId);
        setServiceId(next.serviceId);
        setInputs(next.inputs);
        setEmails(summary || null);
    };

    const run = async <T,>(label: string, operation: () => Promise<T>) => {
        if (busy)
            return;
        setBusy(label);
        setError('');
        setMessage('');
        try { return await operation(); } catch (failure: any) { setError(failure.message || 'The report action failed.'); }
        finally { setBusy(''); }
    };

    const open = (draftId: string) => run('load', async () => {
        const result = await consultantReports<{ draft: ReportDraft; emails: ReportEmailSummary }>('load', { draftId });
        accept(result.draft, result.emails);
        generation.current = null;
    });
    const reset = () => {
        setDraft(null);
        setReport(null);
        setOrganizationId(clientOrganizationId);
        setServiceId('');
        setInputs(emptyReportInputs);
        setEmails(null);
        setError('');
        setMessage('');
        generation.current = null;
    };
    const create = (draftId: string) => run('create', async () => {
        const result = await consultantReports<{ draft: ReportDraft }>('create', { draftId, organizationId, serviceId, inputs });
        await loadList();
        return result.draft;
    });
    const generate = () => run('generate', async () => {
        if (geography.blocked)
            throw new Error('Confirm or correct the primary target country first.');
        const operation = { draftId: draft?.id || generation.current?.draftId || crypto.randomUUID(), operationId: crypto.randomUUID() };
        generation.current = operation;
        try {
            const result = await consultantReports<{ draft: ReportDraft }>('generate', { ...operation,
                organizationId, serviceId, inputs, geographicConfirmation: geography.confirmation, version: draft?.version || 0 });
            accept(result.draft);
        } catch (failure) {
            const recovered = await consultantReports<{ draft: ReportDraft }>('load', { draftId: operation.draftId }).catch(() => null);
            if (recovered)
                accept(recovered.draft);
            throw failure;
        } finally {
            await loadList();
        }
    });
    const save = () => run('save', async () => {
        const result = await consultantReports<{ draft: ReportDraft }>('save', { draftId: draft!.id, version: draft!.version, report });
        accept(result.draft);
        setInputs(inputs);
        setMessage('Draft changes saved. Review before delivery.');
        await loadList();
    });
    const deliver = (destination: Record<string, unknown>) => run('deliver', async () => {
        if (geography.blocked)
            throw new Error('Confirm or correct the primary target country first.');
        const result = await consultantReports<{ draft: ReportDraft; emails: ReportEmailSummary }>('deliver', {
            draftId: draft!.id, version: draft!.version, geographicConfirmation: geography.confirmation, ...destination });
        accept(result.draft, result.emails);
        setMessage('Report delivered. Client email notifications are queued.');
        await loadList();
    });
    const retryEmails = () => run('emails', async () => {
        const result = await consultantReports<{ emails: ReportEmailSummary }>('retryEmails', { draftId: draft!.id });
        setEmails(result.emails);
        setMessage('Failed emails queued for retry.');
    });
    return { organizations, services, drafts: clientOrganizationId ? drafts.filter(item => item.organizationId === clientOrganizationId) : drafts,
        draft, organizationId, setOrganizationId, serviceId, setServiceId,
        inputs, setInputs, report, setReport, targets, emails, busy, error, message, loading, dirty,
        geography, inputsChanged, open, reset, create, refresh: () => run('refresh', loadList), generate, save, deliver, retryEmails };
}

export type ConsultantReportModel = ReturnType<typeof useConsultantReports>;
